/**
 * The host's status strip and action toast — what the web host draws above and over a hosted app. Same states and
 * the same i18n keys on iOS and Android (`apps.hosted.*`), so the rules are here, pure, and pinned by jest.
 *
 * ONE strip at a time, most important first: the app is gone > you are offline > writes have not landed >
 * still opening > the EDIT_OWN hint (a standing note, shown only when nothing more urgent is).
 */

export type HostStrip =
  | { kind: 'revoked'; key: 'apps.hosted.revoked' | 'apps.hosted.revokedUnknown'; params?: { name: string } }
  | { kind: 'deleted'; key: 'apps.hosted.deleted' }
  | { kind: 'offline'; key: 'apps.hosted.offline' }
  | { kind: 'pending'; key: 'apps.hosted.pending.one' | 'apps.hosted.pending.other'; params: { count: number } }
  | { kind: 'loading'; key: 'apps.hosted.loading' }
  | { kind: 'editOwn'; key: 'apps.hosted.editOwnHint' };

export interface HostStripInput {
  /** The app answered `ping` on this frame load. */
  handshaken: boolean;
  online: boolean;
  pendingWrites: number;
  /** A data call answered NOT_FOUND after the app had opened. */
  gone: boolean;
  role: 'OWNER' | 'MEMBER';
  dataAccess: 'EDIT_ALL' | 'EDIT_OWN' | null;
  ownerName: string;
}

export function hostStrip(input: HostStripInput): HostStrip | null {
  if (input.gone) {
    // NOT_FOUND is indistinguishable on purpose (revoked, deleted, pending). An owner's own app cannot be
    // un-shared from them, so for an owner it was deleted; for a member, the owner stopped sharing it — the
    // likelier fact, and the one the member can do something about (ask).
    if (input.role === 'OWNER') return { kind: 'deleted', key: 'apps.hosted.deleted' };
    const name = input.ownerName.trim();
    return name ? { kind: 'revoked', key: 'apps.hosted.revoked', params: { name } } : { kind: 'revoked', key: 'apps.hosted.revokedUnknown' };
  }
  if (!input.online) return { kind: 'offline', key: 'apps.hosted.offline' };
  if (input.pendingWrites > 0) {
    return {
      kind: 'pending',
      key: input.pendingWrites === 1 ? 'apps.hosted.pending.one' : 'apps.hosted.pending.other',
      params: { count: input.pendingWrites },
    };
  }
  if (!input.handshaken) return { kind: 'loading', key: 'apps.hosted.loading' };
  if (input.role === 'MEMBER' && input.dataAccess === 'EDIT_OWN') return { kind: 'editOwn', key: 'apps.hosted.editOwnHint' };
  return null;
}

export type ActionToast = { key: string; params?: Record<string, string>; severity: 'success' | 'info' | 'error' };

/**
 * The toast the HOST shows after `action.run` (the app shows nothing of its own). STARTED names whose agent took
 * it: the owner's, because every action runs on the owner's agent.
 */
export function actionToast(status: unknown, role: 'OWNER' | 'MEMBER', ownerName: string): ActionToast | null {
  if (status === 'ALREADY_RUNNING') return { key: 'apps.hosted.toast.alreadyRunning', severity: 'info' };
  if (status === 'LIMIT_REACHED') return { key: 'apps.hosted.toast.limitReached', severity: 'error' };
  if (status !== 'STARTED') return null;
  if (role === 'OWNER') return { key: 'apps.hosted.toast.startedOwn', severity: 'success' };
  const name = ownerName.trim();
  return name
    ? { key: 'apps.hosted.toast.started', params: { name }, severity: 'success' }
    : { key: 'apps.hosted.toast.startedUnknown', severity: 'success' };
}

/** The inline offer's title: "<Owner> shared <App> with you", or without the owner when it did not resolve. */
export function offerTitle(appName: string, ownerName: string | undefined): { key: string; params: Record<string, string> } {
  const owner = ownerName?.trim();
  return owner
    ? { key: 'apps.hosted.offer.title', params: { owner, app: appName } }
    : { key: 'apps.hosted.offer.titleUnknown', params: { app: appName } };
}
